import { addMilliseconds, parseISO, subMilliseconds } from 'date-fns'
import { formatInTimeZone, getTimezoneOffset as getTimezoneOffsetFNS, zonedTimeToUtc } from 'date-fns-tz'

/**
 * Check if an ISO string already has a timezone offset (Z, +00:00, -07:00, etc.)
 * If not, we should treat it as UTC by appending 'Z'
 */
const hasTimezoneOffset = (dateStr: string): boolean => {
  // Matches Z, +HH:MM, -HH:MM, +HHMM, -HHMM at the end of the string
  return /([Zz]|[+-]\d{2}:?\d{2})$/.test(dateStr)
}

/**
 * Normalize an ISO string to ensure it has timezone information.
 * - If it already has an offset (Z, +00:00, -07:00), return as-is
 * - If no offset, append 'Z' to treat it as UTC
 */
const normalizeISOString = (dateStr: string): string => {
  return hasTimezoneOffset(dateStr) ? dateStr : dateStr + 'Z'
}

/**
 * Get the timezone offset in milliseconds for a given timezone and date string.
 *
 * @param timeZone - Target timezone (e.g., "America/Los_Angeles")
 * @param dateStr - ISO date string (with or without timezone offset, e.g., "2026-03-21T09:00:00-07:00" or "2026-03-21T16:00:00Z")
 * @returns Offset in milliseconds (negative for timezones behind UTC, e.g., -25200000 for PDT)
 */
export const getTimezoneOffset = (timeZone: string, dateStr: string): number => {
  const date = parseISO(normalizeISOString(dateStr))
  return getTimezoneOffsetFNS(timeZone, date)
}

/**
 * Convert a UTC ISO string to a local Date for display in a given timezone.
 * This adjusts the timestamp so that when displayed without timezone conversion,
 * it shows the time in the target timezone.
 *
 * @param utcDateStr - ISO string (with or without timezone offset, e.g., "2026-03-21T16:00:00Z" or "2026-03-21T09:00:00-07:00")
 * @param timeZone - Target timezone (e.g., "America/Los_Angeles")
 * @returns Date adjusted for the target timezone
 */
export const toLocalDate = (utcDateStr: string, timeZone: string): Date => {
  const date = parseISO(normalizeISOString(utcDateStr))
  const offset = getTimezoneOffsetFNS(timeZone, date)
  return subMilliseconds(date, offset)
}

/**
 * Convert a UTC ISO string to a local ISO string for display in a given timezone.
 * This adjusts the timestamp so that when displayed without timezone conversion,
 * it shows the time in the target timezone.
 *
 * @param utcDateStr - ISO string (with or without timezone offset, e.g., "2026-03-21T16:00:00Z" or "2026-03-21T09:00:00-07:00")
 * @param timeZone - Target timezone (e.g., "America/Los_Angeles")
 * @returns ISO string adjusted for the target timezone
 */
export const toLocalISOString = (utcDateStr: string, timeZone: string): string => {
  return toLocalDate(utcDateStr, timeZone).toISOString()
}

/**
 * Convert an ISO string to a "display" ISO string for FullCalendar.
 * FullCalendar strips timezone info, so we need to shift the UTC time by the target
 * timezone offset so the displayed time matches the local time.
 *
 * Example: "2026-03-21T09:00:00-07:00" (9 AM PDT = 16:00 UTC) with timeZone "America/Los_Angeles"
 *   → parseISO gives 16:00 UTC
 *   → offset for PDT is -7 hours (-25200000 ms)
 *   → 16:00 UTC + (-7 hours) = 09:00 UTC
 *   → Returns "2026-03-21T09:00:00.000Z" so FullCalendar displays "9 AM"
 *
 * @param dateStr - ISO string (with or without timezone offset)
 * @param timeZone - Target timezone (e.g., "America/Los_Angeles")
 * @returns ISO string with time shifted to match local display
 */
export const toUTCISOString = (dateStr: string, timeZone: string): string => {
  const date = parseISO(normalizeISOString(dateStr))
  const offset = getTimezoneOffsetFNS(timeZone, date)
  return addMilliseconds(date, offset).toISOString()
}

// A utility to get a full time zone name such as 'Pacific Daylight Time' or
// an abbreviation such as 'PDT' given a time zone (e.g. 'America/Los_Angeles'),
// date, locale, and time zone name format
export const getTimeZoneName = ({
  date = new Date(),
  locale = 'en-US',
  timeZone,
  timeZoneName = 'long',
}: {
  date?: Date
  locale?: string
  timeZone: string
  timeZoneName?: Intl.DateTimeFormatOptions['timeZoneName']
}) => {
  // The full time zone name or abbreviation is generated by formatting a date into an array representation of
  // the date string that contains the full time zone name or abbreviation and extracting the name or abbreviation
  // from the array.
  // E.g. `parts` would look like this: [{type: 'day', value: '12'}, {type: 'literal', value: ', '}, {type: 'timeZoneName', value: 'Pacific Daylight Time'}]
  // Then the full time zone name or abbreviation can be found from the value of the object with type 'timeZoneName'.
  const parts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    timeZone,
    timeZoneName,
  }).formatToParts(date)
  const longTimeZoneName = parts?.find(({ type }) => type === 'timeZoneName')?.value

  return longTimeZoneName
}

/**
 * Split ISO string into date and time values without converting timezone
 *
 * This function takes an ISO8601 datetime string (without timezone info) and a timezone,
 * and splits it into separate date and time components while treating the input as if
 * it's already in the specified timezone.
 *
 * No timezone conversion or translation should be happening for this method.
 *
 * @param iso8601DateString - ISO8601 datetime string without timezone (e.g., "2025-08-07T18:00:00")
 * @param timezone - Target timezone (e.g., "UTC", "America/New_York")
 * @returns Object with date and time components
 *
 * @example
 * ```typescript
 * const result = splitISO8601String({
 *   iso8601DateString: "2025-08-07T18:00:00",
 *   timezone: "UTC"
 * })
 * // Returns: { date: "2025-08-07", time: "18:00:00" }
 * ```
 */
export const splitISO8601String = ({
  iso8601DateString,
  timezone,
}: {
  iso8601DateString: string
  timezone: string
}) => {
  // `zonedTimeToUtc` takes a separate datetime string (with no timezone) and a timezone and marries them together
  // into a Date object that represents a fully formed datetime.
  const utcDate = zonedTimeToUtc(iso8601DateString, timezone)
  const builtDate = formatInTimeZone(utcDate, timezone, 'yyyy-MM-dd')
  const builtTime = formatInTimeZone(utcDate, timezone, 'HH:mm:ss')
  return {
    date: builtDate,
    time: builtTime,
  }
}
